import React, { useEffect, useState } from 'react';

import { Anchor, Button, Group, Space, Text, Title } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { useForm } from '@mantine/form';
import { RichTextEditor } from '@mantine/tiptap';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import Drawer from 'components/Drawer';
import useAthleteFile from 'hooks/athleteFile/useAthleteFile';
import useCreateFile from 'hooks/athleteFile/useCreateFile';
import useUpdateFile from 'hooks/athleteFile/useUpdateFile';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import DropFiles from './DropFiles';

interface FormValues {
  description: string;
  athlete: string;
  file: File | string | null;
}

const UploadFileForm = ({
  opened,
  athleteId,
  fileId,
  closeHandler,
  setFileId,
  permission = true,
}: {
  opened: boolean;
  athleteId: string;
  fileId?: string | null;
  closeHandler: () => void;
  setFileId: (id: string | null) => void;
  permission?: boolean;
}): JSX.Element => {
  const createFile = useCreateFile();
  const updateFile = useUpdateFile();
  const { data: fileData } = useAthleteFile(fileId || undefined);
  const [currentFile, setCurrentFile] = useState<File>();
  const { successNotification, errorNotification } = notificationHandler();
  const form = useForm<FormValues>({
    initialValues: {
      description: '',
      athlete: athleteId,
      file: null,
    },
    validate: {
      description: value => (value.length < 1 ? 'Description cannot be empty' : null),
    },
  });

  const editor = useEditor({
    extensions: [StarterKit as AnyExtension],
    content: form.getInputProps('description').value,
    onUpdate: ({ editor: _editor }) => form.setFieldValue('description', _editor.getText()),
  });

  const handleCloseAndReset = (): void => {
    setFileId(null);
    form.reset();
    editor?.commands.setContent('');
    closeHandler();
  };

  const handleFileChange = (file: FileWithPath[]): void => {
    if (file[0] && file[0].size > 5000000) {
      errorNotification('The selected file is too large. Please select a file smaller than 5MB.');
    } else if (file[0]) {
      form.setFieldValue('file', file[0]);
      setCurrentFile(file[0]);
    } else {
      errorNotification('Could not upload a file');
    }
  };

  const handleSubmit = (values: FormValues): void => {
    if (!fileId && currentFile) {
      const payload = {
        description: values.description,
        fileId: uuidv4(),
        fileType: currentFile.type.includes('image') ? 'PICTURE' : 'PDF',
        file: currentFile,
        athlete: athleteId,
      };

      createFile.mutate(payload, {
        onSuccess: () => {
          successNotification('File created successfully');
        },
        onError: error => {
          errorNotification('File create failed', error.message);
        },
      });
    } else if (fileData) {
      updateFile.mutate(
        {
          ...fileData.data,
          description: values.description,
          athlete: athleteId,
          fileType: currentFile
            ? currentFile?.type.includes('image')
              ? 'PICTURE'
              : 'PDF'
            : fileData.data.fileType,
          file: currentFile ? currentFile : null,
        },
        {
          onSuccess: () => {
            successNotification('File updated successfully');
          },
          onError: error => {
            errorNotification('File update failed', error.message);
          },
        },
      );
    }

    handleCloseAndReset();
  };

  useEffect(() => {
    if (fileData) {
      editor?.commands.setContent(fileData.data.description);
      form.setFieldValue('description', fileData.data.description!);
      form.setFieldValue('file', fileData.data.file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileData]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size='lg'
      title={fileId ? <Title order={3}>View File</Title> : <Title order={3}>Create File</Title>}
      footer={
        <Group position='apart' align='center'>
          <Button onClick={handleCloseAndReset}>Cancel</Button>
          <Button
            type='submit'
            form='note-create-form'
            disabled={form.values.description.length < 1 || !!!form.values.file || !permission}
          >
            Save
          </Button>
        </Group>
      }
    >
      <Text>Description</Text>
      <form id='note-create-form' onSubmit={form.onSubmit(handleSubmit)}>
        <RichTextEditor editor={editor}>
          <RichTextEditor.Content />
        </RichTextEditor>
        <Space h='lg' />
        <Text>Upload a file</Text>
        {!form.values.file ? (
          <DropFiles handleFileChange={handleFileChange} />
        ) : currentFile ? (
          <div>
            {currentFile.type.includes('image') ? (
              <img
                src={URL.createObjectURL(currentFile)}
                alt='file'
                style={{ width: '100%', height: '100%', marginTop: '10px' }}
              />
            ) : (
              <Anchor href={URL.createObjectURL(currentFile)} target='_blank' rel='noreferrer'>
                {currentFile.name}
              </Anchor>
            )}
            <Button
              sx={{ display: 'block', marginTop: '10px' }}
              onClick={() => form.setFieldValue('file', null)}
            >
              Replace this file
            </Button>
          </div>
        ) : (
          <div>
            {fileData?.data.fileType === 'PICTURE' ? (
              <img
                src={typeof form.values.file === 'string' ? form.values.file : ''}
                alt='file'
                style={{ width: '100%', height: '100%', marginTop: '10px' }}
              />
            ) : (
              fileData?.data.file && (
                <Anchor href={fileData.data.file} target='_blank' rel='noreferrer'>
                  {fileData.data.filename}
                </Anchor>
              )
            )}
            <Button
              sx={{ display: 'block', marginTop: '10px' }}
              onClick={() => form.setFieldValue('file', null)}
            >
              Replace this file
            </Button>
          </div>
        )}
      </form>
    </Drawer>
  );
};

export default UploadFileForm;
